import React, { useState, useEffect } from 'react';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { Spinner, EstadoBadge } from '../components/UI';
import {
  CARD, CARD_TITLE, PRESETS, rangoPreset,
  fmt, fmtEntero, KpiCard, DonutEstados, AreaChart, AgingCard, BarList,
} from './Dashboard';

const SELECT_STYLE = {
  padding: '7px 10px', borderRadius: 8, border: '1px solid var(--hair)', background: 'var(--panel)',
  fontSize: 12.5, color: 'var(--ink)', fontFamily: 'inherit', outline: 'none',
};

// Dashboard personal del solicitante: SOLO sus propias solicitudes de pago.
export default function MiDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [preset, setPreset] = useState('todo');
  const [rango, setRango] = useState({ desde: '', hasta: '' });

  useEffect(() => {
    setLoading(true);
    const qs = Object.entries(rango).filter(([, v]) => v).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&');
    api.get(`/dashboard/mias${qs ? `?${qs}` : ''}`)
      .then(d => { setData(d); setError(false); })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [rango]);

  const aplicarPreset = (id) => { setPreset(id); setRango(rangoPreset(id)); };

  const porEstadoMap = {};
  (data?.porEstado || []).forEach(d => { porEstadoMap[d._id] = d; });
  const totalMonto = data?.totales?.monto || 0;
  const totalCount = data?.totales?.count || 0;
  const pagado = porEstadoMap.pagada || { count: 0, monto: 0 };
  const pendiente = porEstadoMap.pendiente || { count: 0, monto: 0 };
  const enProceso = ['aprobada', 'por_pagar'].reduce((s, e) => ({
    count: s.count + (porEstadoMap[e]?.count || 0),
    monto: s.monto + (porEstadoMap[e]?.monto || 0),
  }), { count: 0, monto: 0 });

  return (
    <div style={{ fontFamily: 'Inter, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: 'var(--ink)', letterSpacing: '-0.02em' }}>Mis Solicitudes</h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--glass)' }}>
            {loading && !data ? 'Cargando…' : <>Hola {user?.nombre?.split(' ')[0] || ''} · {totalCount} solicitud{totalCount !== 1 ? 'es' : ''} · ${fmt(totalMonto)} MXN</>}
          </p>
        </div>
        {/* Presets de fecha */}
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 4, background: 'var(--hair-soft)', padding: 3, borderRadius: 10 }}>
            {PRESETS.map(p => (
              <button key={p.id} onClick={() => aplicarPreset(p.id)}
                style={{
                  padding: '5px 11px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                  fontFamily: 'inherit', transition: 'all 0.12s',
                  background: preset === p.id ? 'var(--panel-hi)' : 'transparent', boxShadow: preset === p.id ? 'inset 0 -2px var(--plasma)' : 'none',
                  color: preset === p.id ? 'var(--plasma)' : 'var(--glass)',
                }}>
                {p.label}
              </button>
            ))}
          </div>
          <input type="date" value={rango.desde} onChange={e => { setPreset(''); setRango(r => ({ ...r, desde: e.target.value })); }} style={{ ...SELECT_STYLE, width: 132 }} />
          <span style={{ color: 'var(--glass)', fontSize: 12 }}>→</span>
          <input type="date" value={rango.hasta} onChange={e => { setPreset(''); setRango(r => ({ ...r, hasta: e.target.value })); }} style={{ ...SELECT_STYLE, width: 132 }} />
        </div>
      </div>

      {error && <div style={{ padding: 40, color: 'var(--glass)', textAlign: 'center' }}>No se pudo cargar tu dashboard.</div>}
      {loading && !data && <div style={{ padding: 60, display: 'flex', justifyContent: 'center' }}><Spinner /></div>}

      {data && !error && (
        <div style={{ opacity: loading ? 0.55 : 1, transition: 'opacity 0.15s' }}>
          {totalCount === 0 ? (
            <div style={{ ...CARD, padding: '48px 32px', textAlign: 'center' }}>
              <div style={{ fontSize: 30, marginBottom: 8 }}>📭</div>
              <div style={{ fontSize: 14, color: 'var(--ink-soft)', fontWeight: 600, marginBottom: 4 }}>Aún no tienes solicitudes en este periodo</div>
              <div style={{ fontSize: 12.5, color: 'var(--glass)' }}>Crea una desde "＋ Nueva Solicitud" y aquí verás cómo avanza.</div>
            </div>
          ) : (
            <>
              {/* KPIs por estado */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14, marginBottom: 18 }}>
                <KpiCard label="Total solicitado" icon="💰" color="#5e72e4" value={`$${fmtEntero(totalMonto)}`} sub={`${totalCount} solicitud${totalCount !== 1 ? 'es' : ''}`} />
                <KpiCard label="Pendientes" icon="⏳" color="#f59e0b" value={`$${fmtEntero(pendiente.monto)}`} sub={`${pendiente.count} esperando aprobación`} />
                <KpiCard label="En proceso" icon="📤" color="#8b5cf6" value={`$${fmtEntero(enProceso.monto)}`} sub={`${enProceso.count} aprobadas / por pagar`} />
                <KpiCard label="Pagadas" icon="💳" color="#3b82f6" value={`$${fmtEntero(pagado.monto)}`} sub={totalMonto > 0 ? `${((pagado.monto / totalMonto) * 100).toFixed(1)}% del total` : '—'} />
              </div>

              {/* Evolución + Distribución por estado */}
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(300px, 1fr)', gap: 18, marginBottom: 18 }}>
                <AreaChart serie={data.serie} granularidad={data.granularidad} />
                <DonutEstados data={data.porEstado} activo={null} />
              </div>

              {/* Antigüedad de pendientes + Gasto por motivo */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 18, marginBottom: 18 }}>
                <AgingCard data={data.aging} />
                <BarList titulo="Mi gasto por motivo" data={data.porMotivo} activo={null} />
              </div>

              {/* Mis solicitudes recientes */}
              <div style={CARD}>
                <div style={CARD_TITLE}><span>🕐 Mis solicitudes recientes</span></div>
                <div>
                  {(data.recientes || []).length === 0 ? (
                    <div style={{ padding: 24, textAlign: 'center', color: 'var(--glass)', fontSize: 13 }}>Sin solicitudes</div>
                  ) : data.recientes.map((s, i) => {
                    return (
                      <div key={s._id || i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 18px', borderBottom: '1px solid var(--hair-soft)' }}
                        onMouseEnter={ev => ev.currentTarget.style.background = 'rgba(255,84,28,0.04)'}
                        onMouseLeave={ev => ev.currentTarget.style.background = 'transparent'}
                      >
                        <div style={{ minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontFamily: 'Geist Mono, monospace', fontSize: 11, color: 'var(--plasma)', fontWeight: 700 }}>{s.folio}</span>
                            <EstadoBadge estado={s.estado} />
                          </div>
                          <div style={{ fontSize: 12, color: 'var(--glass)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {s.proveedor} · {s.empresa}{s.sucursal ? ` · ${s.sucursal}` : ''}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 10 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink)', fontFamily: 'Geist Mono, monospace' }}>${fmt(s.monto)}</div>
                          <div style={{ fontSize: 10, color: 'var(--glass)' }}>{new Date(s.createdAt).toLocaleDateString('es-MX')}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
